import { ISSUE_MGMT_WRITE_PERMISSION, type TodoCard } from '@cortex/domain-ac-mens-hub-api';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2, Clock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ExcludedPanel } from '../components/issue-mgmt/ExcludedPanel.js';
import { InspectionPanel } from '../components/issue-mgmt/InspectionPanel.js';
import { ToggleSwitch } from '../components/issue-mgmt/ToggleSwitch.js';
import { TodoCardList, TodoDue } from '../components/issue-mgmt/TodoCardList.js';
import { TodoIssueCard } from '../components/issue-mgmt/TodoIssueCard.js';
import { TodoTriageCard } from '../components/issue-mgmt/TodoTriageCard.js';
import { useIssueToast } from '../components/issue-mgmt/useIssueToast.js';
import { useIssueMgmtWrite } from '../hooks/useIssueMgmt.js';
import { useTodoCards } from '../hooks/useIssueTodo.js';
import { usePermissions } from '../hooks/usePermissions.js';
import { ISSUE_WEEKDAY_LABEL_KEYS } from '../lib/issue-mgmt-format.js';
import { TODO_TYPE_LABEL_KEYS } from '../lib/issue-mgmt-next-action.js';
import {
  countLeft,
  type DoneCard,
  filterTodoRows,
  mergeDoneCards,
  nextOpenCardId,
  type TodoFilter,
} from '../lib/issue-mgmt-todo.js';

/**
 * 絞り込みの並び。
 * @graph-connects none
 */
const FILTERS: readonly TodoFilter[] = ['all', 'triage', 'judge', 'check'];

/**
 * 絞り込みの文言キー。
 * @graph-connects none
 */
const FILTER_LABEL_KEYS: Record<TodoFilter, string> = {
  all: 'issueMgmt.todo.filterAll',
  ...TODO_TYPE_LABEL_KEYS,
};

/**
 * やることの URL の条件（開くカードと、最下段に開く一覧）。
 * @graph-connects none
 */
interface TodoSearch {
  card: string | null;
  panel: 'excluded' | 'inspection' | null;
}

/**
 * URL の条件を読む（知らない値は無いものとして扱う）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることの URL から、開くカードと最下段に開く一覧（除外・点検）を読む
 * @graph-connects none
 */
export function parseTodoSearch(search: Record<string, unknown>): TodoSearch {
  const panel = search.panel;
  return {
    card: typeof search.card === 'string' && search.card !== '' ? search.card : null,
    panel: panel === 'excluded' || panel === 'inspection' ? panel : null,
  };
}

/**
 * 課題管理の「やること」タブ（SC-01）。振り分け・判定・確認を、左の一覧から1枚ずつ右のカードで片づける。
 *
 * 上に絞り込み（すべて・振り分け・判定・確認と残りの数）・自分の分だけ・週次の案内（押すと判定で絞る）、
 * 左にカードの一覧、右にカード1枚、最下段に AI が除外した投稿（点検のときは点検の一覧）。カードを押して片づけると、
 * 次の済みでないカードへ進み、済んだカードは開き直すまで取り消し線で残ってカードからも元に戻せる。
 * 編集キーの人だけの画面（閲覧キーだけの人にはタブを出さず、開いても案内だけを出す）。
 *
 * @graph-node {Module}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 当番・判定者・担当が、声の振り分け・課題の判定・案件や直ったかの確認をカード1枚ずつ片づけ、残りを0にする画面
 * @graph-connects useTodoCards [calls] やることのカード
 * @graph-connects useIssueMgmtWrite [calls] undo
 * @graph-connects TodoCardList [calls] 左の一覧
 * @graph-connects TodoTriageCard [calls] 振り分けのカード
 * @graph-connects TodoIssueCard [calls] 判定・確認のカード
 * @graph-connects ExcludedPanel [calls] AI が除外した投稿
 * @graph-connects InspectionPanel [calls] 月1回の点検
 */
export function IssueMgmtTodoPage({ now = new Date() }: { now?: Date }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = parseTodoSearch(useSearch({ strict: false }));
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canEdit = !permissionsLoading && hasPermission(ISSUE_MGMT_WRITE_PERMISSION);
  const todo = useTodoCards(canEdit);
  const undo = useIssueMgmtWrite('undo');
  const toast = useIssueToast();
  const [filter, setFilter] = useState<TodoFilter>('all');
  const [mine, setMine] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [done, setDone] = useState<ReadonlyMap<string, DoneCard>>(new Map());

  const setSearch = (next: TodoSearch) => {
    void navigate({
      to: '/issue-mgmt/todo',
      search: {
        ...(next.card === null ? {} : { card: next.card }),
        ...(next.panel === null ? {} : { panel: next.panel }),
      },
      replace: true,
    });
  };

  if (!permissionsLoading && !canEdit) {
    return (
      <p className="rounded-xl border border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-muted)]">
        {t('issueMgmt.todo.noPermission')}
      </p>
    );
  }
  if (todo.isError) {
    return (
      <div className="space-y-2 rounded-xl border border-[var(--color-border)] p-6 text-center">
        <p className="text-sm text-[var(--color-error)]">{t('issueMgmt.common.loadError')}</p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void todo.refetch();
          }}
        >
          {t('issueMgmt.common.retry')}
        </Button>
      </div>
    );
  }
  if (todo.data === undefined) {
    return (
      <p className="py-10 text-center text-sm text-[var(--color-text-muted)]">
        {t('issueMgmt.common.loading')}
      </p>
    );
  }

  const cards = todo.data.cards;
  const allRows = mergeDoneCards(cards, done);
  const rows = filterTodoRows(allRows, filter, mine);
  const counts = countLeft(allRows, mine);
  const requested = selected ?? search.card;
  const current =
    rows.find((row) => row.card.cardId === requested) ??
    rows.find((row) => row.done === null) ??
    rows[0] ??
    null;
  const weekly = todo.data.weekly;

  const select = (cardId: string | null) => {
    setSelected(cardId);
  };

  const outcomeFor = (card: TodoCard) => ({
    onDone: (label: string, operationId: string | null) => {
      const index = allRows.findIndex((row) => row.card.cardId === card.cardId);
      const nextDone = new Map(done);
      nextDone.set(card.cardId, { card, label, operationId, index });
      setDone(nextDone);
      const nextRows = filterTodoRows(mergeDoneCards(cards, nextDone), filter, mine);
      select(nextOpenCardId(nextRows, card.cardId));
    },
    onConflict: () => {
      select(nextOpenCardId(rows, card.cardId));
    },
  });

  const undoCard = (entry: DoneCard) => {
    if (entry.operationId === null) return;
    undo.mutate(
      { operationId: entry.operationId },
      {
        onSuccess: () => {
          const nextDone = new Map(done);
          nextDone.delete(entry.card.cardId);
          setDone(nextDone);
          toast.notifySaved(t('issueMgmt.toast.undone'));
        },
        onError: toast.notifyError,
      },
    );
  };

  const clearFilters = () => {
    setFilter('all');
    setMine(false);
  };
  const leftShown = rows.filter((row) => row.done === null).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="group"
          aria-label={t('issueMgmt.todo.filterLabel')}
          className="inline-flex overflow-hidden rounded-lg border border-[var(--color-border)]"
        >
          {FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => {
                setFilter(value);
              }}
              className={`px-3 py-1.5 text-sm ${
                filter === value
                  ? 'bg-[var(--color-accent-primary)] font-semibold text-white'
                  : 'bg-[var(--color-bg-surface)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              {t(FILTER_LABEL_KEYS[value])} {counts[value]}
            </button>
          ))}
        </div>
        <ToggleSwitch
          checked={mine}
          onToggle={() => {
            setMine((value) => !value);
          }}
        >
          {t('issueMgmt.todo.mine')}
        </ToggleSwitch>
        <span className="ml-auto inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)]">
          <Clock className="h-4 w-4" aria-hidden="true" />
          {t('issueMgmt.todo.weekly', { weekday: t(ISSUE_WEEKDAY_LABEL_KEYS[weekly.weekday]) })}
          <button
            type="button"
            onClick={() => {
              setFilter('judge');
            }}
            className={`font-semibold hover:underline ${weekly.isToday ? 'text-[var(--color-error)]' : 'text-[var(--color-accent-primary)]'}`}
          >
            {t('issueMgmt.todo.weeklyJudge', { n: String(weekly.judgeCount) })}
          </button>
        </span>
      </div>

      {leftShown === 0 ? (
        <div className="space-y-3 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-10 text-center">
          <CheckCircle2
            className="mx-auto h-8 w-8 text-[var(--color-success)]"
            aria-hidden="true"
          />
          <p className="text-lg font-semibold text-[var(--color-text-primary)]">
            {t('issueMgmt.todo.allDone')}
          </p>
          {filter !== 'all' || mine ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-[var(--color-accent-primary)] hover:underline"
            >
              {t('issueMgmt.todo.showOthers')}
            </button>
          ) : (
            <Link
              to="/issue-mgmt/issues"
              className="text-sm text-[var(--color-accent-primary)] hover:underline"
            >
              {t('issueMgmt.todo.openIssues')}
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[18rem_minmax(0,1fr)]">
          <TodoCardList
            rows={rows}
            selectedId={current?.card.cardId ?? null}
            leftCount={leftShown}
            onSelect={select}
            now={now}
          />
          {current !== null && (
            <article
              key={current.card.cardId}
              className="overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]"
            >
              <div className="flex items-center justify-between border-b border-[var(--color-border)] px-5 py-2 text-xs">
                <span className="font-semibold text-[var(--color-text-secondary)]">
                  {t('issueMgmt.todo.cardOwner', {
                    type: t(TODO_TYPE_LABEL_KEYS[current.card.type]),
                    name: current.card.who?.displayName ?? t('issueMgmt.todo.noOwner'),
                  })}
                </span>
                <TodoDue card={current.card} done={current.done !== null} now={now} />
              </div>
              {current.card.source === 'triage' ? (
                <TodoTriageCard
                  card={current.card}
                  done={current.done !== null}
                  outcome={outcomeFor(current.card)}
                  now={now}
                />
              ) : (
                <TodoIssueCard
                  card={current.card}
                  done={current.done !== null}
                  outcome={outcomeFor(current.card)}
                  now={now}
                />
              )}
              {current.done !== null && (
                <div className="flex items-center gap-3 border-t border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-5 py-3 text-sm">
                  <CheckCircle2
                    className="h-4 w-4 text-[var(--color-success)]"
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-[var(--color-text-primary)]">
                    {current.done.label}
                  </span>
                  {current.done.operationId !== null && (
                    <button
                      type="button"
                      disabled={undo.isPending}
                      onClick={() => {
                        if (current.done !== null) undoCard(current.done);
                      }}
                      className="text-sm text-[var(--color-accent-primary)] hover:underline"
                    >
                      {t('issueMgmt.todo.undo')}
                    </button>
                  )}
                </div>
              )}
            </article>
          )}
        </div>
      )}

      {search.panel === 'inspection' ? (
        <InspectionPanel
          now={now}
          onClose={() => {
            setSearch({ card: null, panel: null });
          }}
        />
      ) : (
        <ExcludedPanel
          open={search.panel === 'excluded'}
          now={now}
          onToggle={() => {
            setSearch({ card: null, panel: search.panel === 'excluded' ? null : 'excluded' });
          }}
        />
      )}
    </div>
  );
}
